import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { IStoreState } from "..";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import {
  getFileNameFromUrl,
  uploadMultipleFile,
} from "../../components/FileUpload/utils";
import { IGateEntry, IGetGateEntryResponse } from "./gate-entry.types";
import { getUniqueId } from "../../helpers";
import produce from "immer";
import { getFileExtensionType } from "../../components/FileUploadIcon/helpers";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";
import { server_base_endpoints } from "../../api/server-base-endpoints";

export const FETCH_GATE_ENTRY_LIST_PROGRESS = "FETCH_GATE_ENTRY_LIST_PROGRESS";
export const FETCH_GATE_ENTRY_LIST_SUCCESS = "FETCH_GATE_ENTRY_LIST_SUCCESS";
export const FETCH_GATE_ENTRY_LIST_FAILED = "FETCH_GATE_ENTRY_LIST_FAILED";

export const fetchGateEntryListProgress = () =>
  action(FETCH_GATE_ENTRY_LIST_PROGRESS);
export const fetchGateEntryListSuccess = (
  data: IGateEntry[],
  totalRecords: number,
) => action(FETCH_GATE_ENTRY_LIST_SUCCESS, { data, totalRecords });
export const fetchGateEntryListFailed = () =>
  action(FETCH_GATE_ENTRY_LIST_FAILED);

export const fetchGateEntryListAsync =
  (queryParams: IQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchGateEntryListProgress());
      let finalUrl = `${server_base_endpoints.grn.get_gate_entry}${searchQuery}`;

      const res = await api.get(finalUrl);
      const data: IGateEntry[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchGateEntryListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchGateEntryListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch gate entry list",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertSingleGateEntryAsync =
  (
    gateEntry: IGateEntry,
    onCallback: (isSuccess: boolean, gateEntry?: IGateEntry) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const {
        place_of_supply,
        transport_doc_date,
        transport_doc_no,
        transport_id,
        dispatch_through,
        delivery_type,
        rev_charges,
        lr_no,
        manager,
        gate_entry_no,
        running_no,
        attachments,
        rowId,
        prefix,
        financial_year,
        dynamic_approval_uuid,
        is_user_approver,
        requested_by_uuid,
        requested_by_name,
        combined_gate_entry_no,
        approval_uuid,
        created_by_uuid,
        created_by_name,
        modified_by_uuid,
        modified_by_name,
        insert_ts,
        create_ts,
        ...rest
      } = gateEntry;
      dispatch(saveLoaderProgress());
      const asPayload = {
        title: "Gate Entry Attachment",
      };

      const { paths } = await uploadMultipleFile(
        attachments,
        "GATE_ENTRY",
        asPayload,
      );

      const res = await api.post(
        `${server_base_endpoints.grn.upsert_gate_entry}`,
        {
          ...rest,
          attachments: paths,
        },
      );
      const data = res.data.data;
      onCallback(true, data);
      dispatch(
        showMessage({
          type: "success",
          message: "Gate Entry saved successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to save gate entry",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_SINGLE_GATE_ENTRY_PROGRESS =
  "FETCH_SINGLE_GATE_ENTRY_PROGRESS";
export const FETCH_SINGLE_GATE_ENTRY_SUCCESS =
  "FETCH_SINGLE_GATE_ENTRY_SUCCESS";
export const FETCH_SINGLE_GATE_ENTRY_FAILED = "FETCH_SINGLE_GATE_ENTRY_FAILED";

export const fetchSingleGateEntryProgress = () =>
  action(FETCH_SINGLE_GATE_ENTRY_PROGRESS);
export const fetchSingleGateEntrySuccess = (data: IGateEntry) =>
  action(FETCH_SINGLE_GATE_ENTRY_SUCCESS, { data });
export const fetchSingleGateEntryFailed = (errorMessage: string) =>
  action(FETCH_SINGLE_GATE_ENTRY_FAILED, { errorMessage });

export const fetchSingleGateEntryAsync =
  (
    uuid: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchSingleGateEntryProgress());
      let url = `${server_base_endpoints.grn.get_gate_entry}?gate_entry_uuid=${uuid}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(url);
      const data: IGetGateEntryResponse[] = res.data.data;
      if (data.length > 0) {
        const { attachments, ...rest } = data[0];
        const newRespponse: IGateEntry = produce(
          { ...rest, attachments: [] as IFileUpload[] },
          (draftState) => {
            if (Array.isArray(attachments) && attachments.length > 0) {
              draftState.attachments = attachments.map((path) => ({
                key: getUniqueId(),
                file: null,
                type: "SERVER",
                name: getFileNameFromUrl(path as unknown as string),
                path: path,
              }));
            }
          },
        );
        dispatch(fetchSingleGateEntrySuccess(newRespponse));
      } else {
        dispatch(
          fetchSingleGateEntryFailed("Oops! We couldn't find any records."),
        );
      }
    } catch (err: any) {
      dispatch(fetchSingleGateEntryFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Error fetching gate entry",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_SINGLE_GATE_ENTRY = "CLEAR_SINGLE_GATE_ENTRY";
export const CLEAR_GATE_ENTRY_STATE = "CLEAR_GATE_ENTRY_STATE";

export const clearSingleGateEntry = () => action(CLEAR_SINGLE_GATE_ENTRY);
export const clearGateEntryState = () => action(CLEAR_GATE_ENTRY_STATE);
